import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getSuggestedAthletes, SuggestedAthleteWithData } from '../services/suggestedAthletesService';
import { followUser as followUserService, unfollowUser as unfollowUserService } from '../services/connections';

// Enhanced suggested athlete interface with UI state
export interface CachedSuggestedAthlete extends SuggestedAthleteWithData {
  isFollowed: boolean;
  isDismissed: boolean;
  cachedAt: number;
}

// Store interface
export interface SuggestedAthletesStore {
  // State
  athletes: CachedSuggestedAthlete[];
  isLoading: boolean;
  loadingOperations: string[]; // Track operations like 'follow-userId', 'dismiss-userId'
  lastRefresh: number;
  cachedAt: number;
  
  // Actions
  loadSuggestedAthletes: (currentUserId: string, followingIds: string[]) => Promise<void>;
  followAthlete: (currentUserId: string, targetUserId: string) => Promise<void>;
  dismissAthlete: (userId: string) => void;
  refreshSuggestions: (currentUserId: string, followingIds: string[]) => Promise<void>;
  updateFollowingStatus: (followingIds: string[]) => void;
  clearExpiredSuggestions: () => void;
  isSuggestionsExpired: () => boolean;
  
  // Getters
  getVisibleAthletes: () => CachedSuggestedAthlete[];
  getAthleteById: (userId: string) => CachedSuggestedAthlete | null;
  isAthleteLoading: (userId: string) => boolean;
  
  // Internal actions
  setAthletes: (athletes: CachedSuggestedAthlete[]) => void;
  updateAthlete: (userId: string, updates: Partial<CachedSuggestedAthlete>) => void;
  addLoadingOperation: (operation: string) => void;
  removeLoadingOperation: (operation: string) => void;
  setLoading: (loading: boolean) => void;
}

// Constants
const CACHE_TTL = 6 * 60 * 60 * 1000; // 6 hours in milliseconds
const CLEANUP_INTERVAL = 30 * 60 * 1000; // 30 minutes in milliseconds

export const useSuggestedAthletesStore = create<SuggestedAthletesStore>()(
  persist(
    (set, get) => ({
      // Initial state
      athletes: [],
      isLoading: false,
      loadingOperations: [],
      lastRefresh: 0,
      cachedAt: 0,

      // Load suggested athletes from Firebase
      loadSuggestedAthletes: async (currentUserId: string, followingIds: string[]) => {
        const state = get();
        
        // Check if already cached and not expired
        if (!state.isSuggestionsExpired() && state.athletes.length > 0) {
          // Update following status with current followingIds
          state.updateFollowingStatus(followingIds);
          return;
        }
        
        // Check if already loading
        if (state.loadingOperations.includes('load-suggestions')) return;
        
        state.addLoadingOperation('load-suggestions');
        
        try {
          console.log('🔄 Loading suggested athletes...');
          
          const athletesData = await getSuggestedAthletes();
          
          // Filter out current user and add UI state
          const athletesWithFlags: CachedSuggestedAthlete[] = athletesData
            .filter(athlete => athlete.userId !== currentUserId)
            .map(athlete => ({
              ...athlete,
              isFollowed: followingIds.includes(athlete.userId),
              isDismissed: false,
              cachedAt: Date.now()
            }));
          
          console.log(`✅ Loaded ${athletesWithFlags.length} suggested athletes`);
          
          set({
            athletes: athletesWithFlags,
            cachedAt: Date.now(),
            lastRefresh: Date.now()
          });
          
        } catch (error) {
          console.error('❌ Error loading suggested athletes:', error);
          throw error;
        } finally {
          state.removeLoadingOperation('load-suggestions');
        }
      },

      // Follow an athlete with optimistic updates
      followAthlete: async (currentUserId: string, targetUserId: string) => {
        const state = get();
        const operationId = `follow-${targetUserId}`;
        
        // Check if already processing this operation
        if (state.loadingOperations.includes(operationId)) return;
        
        state.addLoadingOperation(operationId);
        
        try {
          // Optimistic update - update UI immediately
          state.updateAthlete(targetUserId, { isFollowed: true });
          
          console.log(`🔄 Following suggested athlete: ${targetUserId}`);
          
          // Perform actual Firebase operation
          await followUserService(currentUserId, targetUserId);
          
          console.log(`✅ Successfully followed suggested athlete: ${targetUserId}`);
          
        } catch (error) {
          console.error(`❌ Error following suggested athlete: ${targetUserId}`, error);
          
          // Revert optimistic update on error
          state.updateAthlete(targetUserId, { isFollowed: false });
          
          throw error;
        } finally {
          state.removeLoadingOperation(operationId);
        }
      },

      // Dismiss an athlete (local only - doesn't affect other users)
      dismissAthlete: (userId: string) => {
        const state = get();
        state.updateAthlete(userId, { isDismissed: true });
        console.log(`👋 Dismissed suggested athlete: ${userId}`);
      },

      // Refresh suggestions from Firebase (force reload)
      refreshSuggestions: async (currentUserId: string, followingIds: string[]) => {
        const state = get();
        
        // Clear cache to force fresh load
        set({ 
          athletes: [],
          cachedAt: 0,
          lastRefresh: 0
        });
        
        // Load fresh data
        await state.loadSuggestedAthletes(currentUserId, followingIds);
      },

      // Update following status for all athletes
      updateFollowingStatus: (followingIds: string[]) => {
        const state = get();
        const updatedAthletes = state.athletes.map(athlete => ({
          ...athlete,
          isFollowed: followingIds.includes(athlete.userId)
        }));
        
        set({ athletes: updatedAthletes });
      },

      // Clean up expired suggestions
      clearExpiredSuggestions: () => {
        const state = get();
        
        if (state.isSuggestionsExpired()) {
          console.log('🧹 Clearing expired suggested athletes');
          set({ 
            athletes: [],
            cachedAt: 0,
            lastRefresh: 0
          });
        }
      },

      // Check if suggestions are expired
      isSuggestionsExpired: () => {
        const cachedAt = get().cachedAt;
        if (cachedAt === 0) return true;
        return Date.now() - cachedAt > CACHE_TTL;
      },

      // Get only visible (non-dismissed) athletes
      getVisibleAthletes: () => {
        return get().athletes.filter(athlete => !athlete.isDismissed);
      },

      // Get specific athlete by ID
      getAthleteById: (userId: string) => {
        return get().athletes.find(athlete => athlete.userId === userId) || null;
      },

      // Check if specific athlete is loading (being followed)
      isAthleteLoading: (userId: string) => {
        return get().loadingOperations.includes(`follow-${userId}`);
      },

      // Internal actions
      setAthletes: (athletes: CachedSuggestedAthlete[]) => {
        set({ athletes });
      },

      updateAthlete: (userId: string, updates: Partial<CachedSuggestedAthlete>) => {
        const state = get();
        const updatedAthletes = state.athletes.map(athlete =>
          athlete.userId === userId
            ? { ...athlete, ...updates }
            : athlete
        );
        set({ athletes: updatedAthletes });
      },

      addLoadingOperation: (operation: string) => {
        const currentOps = get().loadingOperations;
        if (!currentOps.includes(operation)) {
          set({ 
            loadingOperations: [...currentOps, operation],
            isLoading: true
          });
        }
      },

      removeLoadingOperation: (operation: string) => {
        const newOps = get().loadingOperations.filter(op => op !== operation);
        set({ 
          loadingOperations: newOps,
          isLoading: newOps.length > 0
        });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      }
    }),
    {
      name: 'suggested-athletes-storage',
      storage: createJSONStorage(() => localStorage),

      // Partial persist - save athletes, timestamps for offline experience
      partialize: (state) => ({
        athletes: state.athletes,
        cachedAt: state.cachedAt,
        lastRefresh: state.lastRefresh
      }),

      // Version for future migrations
      version: 1
    }
  )
);

// Hook for suggested athletes management
export const useSuggestedAthletes = (currentUserId: string | null, followingIds: string[] = []) => {
  const store = useSuggestedAthletesStore();
  
  // Auto-load if not cached and currentUserId provided
  if (currentUserId && (store.isSuggestionsExpired() || store.athletes.length === 0) && !store.loadingOperations.includes('load-suggestions')) {
    store.loadSuggestedAthletes(currentUserId, followingIds);
  }
  
  // Update following status when followingIds change
  if (currentUserId && store.athletes.length > 0) {
    const currentFollowingStatus = store.athletes.map(a => a.isFollowed);
    const newFollowingStatus = store.athletes.map(a => followingIds.includes(a.userId));
    
    // Only update if there's a difference
    if (JSON.stringify(currentFollowingStatus) !== JSON.stringify(newFollowingStatus)) {
      store.updateFollowingStatus(followingIds);
    }
  }
  
  return {
    // Data
    athletes: store.getVisibleAthletes(),
    allAthletes: store.athletes,
    
    // Loading states
    isLoading: store.isLoading || store.loadingOperations.includes('load-suggestions'),
    isFollowLoading: (userId: string) => store.isAthleteLoading(userId),
    
    // Actions
    followAthlete: (userId: string) => currentUserId ? store.followAthlete(currentUserId, userId) : Promise.resolve(),
    dismissAthlete: store.dismissAthlete,
    refresh: () => currentUserId ? store.refreshSuggestions(currentUserId, followingIds) : Promise.resolve(),
    
    // Helpers
    getAthlete: store.getAthleteById,
    isExpired: store.isSuggestionsExpired(),
    lastRefresh: new Date(store.lastRefresh),
    count: store.getVisibleAthletes().length
  };
};

// Hook for suggested athletes utilities
export const useSuggestedAthletesUtils = () => {
  const store = useSuggestedAthletesStore();
  
  return {
    loadSuggestions: store.loadSuggestedAthletes,
    clearExpired: store.clearExpiredSuggestions,
    updateFollowingStatus: store.updateFollowingStatus,
    isLoading: store.isLoading,
    loadingOperations: store.loadingOperations,
    athleteCount: store.athletes.length,
    visibleCount: store.getVisibleAthletes().length,
    isExpired: store.isSuggestionsExpired()
  };
};